import React from "react";
import {
    CommandBar,
    ConstrainMode,
    ContextualMenu,
    ContextualMenuItemType,
    DefaultButton,
    DetailsListLayoutMode,
    Dialog,
    DialogFooter,
    DialogType,
    DirectionalHint,
    IColumn,
    ICommandBarStyles,
    IContextualMenuItem,
    IDetailsRowProps,
    IProgressIndicatorStyles,
    ISearchBoxStyles,
    MarqueeSelection,
    MessageBar,
    MessageBarType,
    Point,
    PrimaryButton,
    ProgressIndicator,
    ScrollablePane,
    ScrollbarVisibility,
    Selection,
    SelectionMode,
    ShimmeredDetailsList,
    mergeStyleSets,
} from "@fluentui/react";
import { useSelector } from "react-redux";
import { LanguageContext } from "../../utilities/LocalizationModule";
import { logger } from "../../utilities/logger";
import { CacheHelper } from "../../utilities/CacheHelper";
import authModule from "../../utilities/AuthModule";
import { OpenPdfInBrowser } from "../../utilities/BrowserUtils";
import { displayHeight, DocubirdCache, extensions, rowLockIndicator } from "../../src/taskpane/config/constants";
import { menuItemIconStyles, previewWebPanelStyles } from "../../src/taskpane/config/controlStyles";
import { RootState } from "../../src/taskpane/components/_v2/redux/storeMain";
import { WebPanel } from "../../src/taskpane/components/_v2/_shared/WebPanel/WebPanel";
import { ArrowClockwise20Regular } from "../../src/taskpane/components/_v2/components/SvgIcons/ArrowClockwise20Regular";
import { ArrowDownload20Regular } from "../../src/taskpane/components/_v2/components/SvgIcons/ArrowDownload20Regular";
import { Delete20Regular } from "../../src/taskpane/components/_v2/components/SvgIcons/Delete20Regular";
import { Filter20Regular } from "../../src/taskpane/components/_v2/components/SvgIcons/Filter20Regular";
import { FilterDismiss20Regular } from "../../src/taskpane/components/_v2/components/SvgIcons/FilterDismiss20Regular";
import { List20Regular } from "../../src/taskpane/components/_v2/components/SvgIcons/List20Regular";
import { defaultFontColor } from "../../src/taskpane/styles/iconStyles";
// The same forked SearchBox the library view's quick filter renders inline in its
// command bar (underlined + leading icon) — NOT Fluent's stock SearchBox.
import { SearchBox as QuickFilterSearchBox } from "../../src/taskpane/components/directoryComponents/SearchBox/SearchBox";
import { ILocation, ISharepointFolderLocation, ISharepointListLocation } from "../Services/LocationService";
import { IAggregatedLibraryDescriptor } from "../Services/AggregatedDocumentsService";
import { getAggregatedSourceKey } from "../Services/aggregatedSourceDescriptors";
import { AGGREGATED_DEFAULT_SORT_KEY, AGGREGATED_MAX_TOTAL_ROWS, useAggregatedDocuments } from "./useAggregatedDocuments";
import { useAggregatedItemHub } from "./useAggregatedItemHub";
import { useClientGroupLibraries } from "./useClientGroupLibraries";
import { MAX_AGGREGATED_GROUP_SITES, boundAggregatedGroupSites, groupSiteDisplayName } from "../Services/clientGroupLibrarySources";
import type { IClientGroupSearchSite } from "../../src/taskpane/components/helpers/sharepointSearch/clientGroupSearchScope";
// Type-only: the library body is a heavy module and this view needs none of its runtime.
import type { IExternalResourceUrl } from "./ContentBodyFilesFolders";
import { useAggregatedCommands } from "./useAggregatedCommands";
import { useAggregatedShareWithGroup } from "./useAggregatedShareWithGroup";
import { useRenameShortcut } from "./hooks/useRenameShortcut";
import { buildAggregateMenuItems } from "./MenuItems/buildAggregateMenuItems";
import { useMenuItem_SelectedFileCount } from "./MenuItems/useMenuItem_SelectedFileCount";
import { CMAggregatedFilterPanel } from "../Panels/CMAggregatedFilterPanel";
import { getBindingForRow } from "../../utilities/AggregatedColumnHelper";
import {
    AGGREGATE_ROW_KEY,
    AGGREGATE_SOURCE_KEY,
    AGGREGATE_SOURCE_LIBRARY_NAME,
    AGGREGATE_SOURCE_LIST_ID,
    AGGREGATE_SOURCE_SITE_NAME,
    AGGREGATE_SOURCE_WEB_URL,
    buildAggregatedGridColumns,
} from "./helpers/aggregateColumnRenderers";
import { getEmptyColumnKeys } from "./helpers/emptyColumnDetection";
import { resolveCommandableItems } from "./helpers/commandableSelection";

/**
 * "All documents" view: one grid over the documents of every visible library on the
 * current site, columns normalized across libraries by display name. Sibling of the
 * locations listing in ContentBodyLibraries, which owns the toggle between the two.
 * On a client-group-connected site the group's sites' libraries join the same list after
 * the site's own documents are shown (with a Site column; see useAggregatedDocuments).
 * Row commands (open/share/checkout/…/move) come from useAggregatedCommands and are
 * wired to each row's SOURCE library.
 */

const classNames = mergeStyleSets({
    // The viewport budget (displayHeight) is shared by the search box, any message
    // bars AND the grid — a fixed grid height would push its bottom edge (and the
    // horizontal scrollbar that lives there) below the fold whenever bars stack.
    viewport: {
        height: displayHeight,
        display: "flex",
        flexDirection: "column",
    },
    wrapper: {
        position: "relative",
        flex: "1 1 auto",
        minHeight: 0,
    },
});

const commandBarStyle: Partial<ICommandBarStyles> = {
    root: {
        borderTop: '1px solid var(--db-border, rgb(237, 235, 233))',
        borderBottom: '1px solid var(--db-border, rgb(237, 235, 233))',
        paddingLeft: 0,
        paddingRight: 10,
        paddingBottom: 3,
        paddingTop: 3
    }
};

// Identical to the library view's quick-filter box (useMenuItem_QuickFilter).
const quickFilterBoxStyles: Partial<ISearchBoxStyles> = {
    root: { width: 300, height: "100%" }
};

// The client-group loading bar: a 2px line over the top edge of the grid, taking no space.
const groupProgressStyles: Partial<IProgressIndicatorStyles> = {
    root: { position: "absolute", top: 0, left: 0, right: 0, zIndex: 2 },
    itemProgress: { padding: 0 },
};

const SEARCH_DEBOUNCE_MS = 500;

const getRowKey = (item: any): string => item?.[AGGREGATE_ROW_KEY];

const hiddenColumnsCacheKey = (siteId: string): string => `allDocumentsHiddenColumns.${siteId}`;

interface IContentBodyAllDocumentsProps {
    siteId: string;
    siteWebUrl: string;
    siteDisplayName?: string;
    libraries: ISharepointListLocation[];
    /**
     * Folder-shaped sources (Tess folder dossiers): each aggregates ONE folder subtree
     * of its parent library, via the descriptor's rootFolderServerRelativeUrl.
     */
    folders?: ISharepointFolderLocation[];
    /**
     * Identity for the persisted view state (hidden columns). Defaults to siteId; Tess
     * locations pass their composite scope because they inherit siteId from the nearest
     * ancestor — per-site keys would bleed across every Tess directory under it.
     */
    viewScopeId?: string;
    isLoadingLibraries: boolean;
    /**
     * The sites grouped with this one (client groups: its own members, and each group it
     * belongs to with that group's other members). Their libraries join the view after the
     * site's own documents are shown; at most MAX_AGGREGATED_GROUP_SITES of them.
     */
    groupSites?: IClientGroupSearchSite[];
    /**
     * The client group lookup that finds `groupSites` has not answered yet. The first paint
     * waits for it within the same hold as for the group sites' libraries.
     */
    isResolvingGroupSites?: boolean;
    /** Re-reads the client group membership, and nothing else of the site (the host applies it to
     * `groupSites`): part of Refresh. */
    onRefreshGroupSites?: () => void;
    /**
     * Deliberate, persisted view switch from the view-switcher menu. Copy/move does NOT
     * route through here: the locations list is shown for the duration of an armed
     * operation by ContentBodyLibraries, so it cannot overwrite the user's preference.
     */
    onSwitchToLocations: () => void;
    onOpenLibrary: (location: ILocation) => void;
}

interface ISortState {
    columnKey: string;
    isSortedDescending: boolean;
}

export const ContentBodyAllDocuments: React.FunctionComponent<IContentBodyAllDocumentsProps> = ({
    siteId,
    siteWebUrl,
    siteDisplayName,
    libraries,
    folders,
    viewScopeId,
    isLoadingLibraries,
    groupSites,
    isResolvingGroupSites = false,
    onRefreshGroupSites,
    onSwitchToLocations,
    onOpenLibrary,
}: IContentBodyAllDocumentsProps) => {
    const languageStrings = React.useContext(LanguageContext);
    const userAccount = useSelector((state: RootState) => state.user?.account);
    const siteSettings = useSelector((state: RootState) => state.tenantInfo?.sites);

    const cacheScopeId = viewScopeId ?? siteId;

    // Client-group sites: the first MAX_AGGREGATED_GROUP_SITES, their libraries read in one
    // Graph batch. A refresh re-reads them too (see refreshView).
    const boundedGroupSites = React.useMemo(() => boundAggregatedGroupSites(groupSites), [groupSites]);
    const [groupRefreshToken, setGroupRefreshToken] = React.useState<number>(0);
    const { libraries: groupLibraries, failedSites: failedGroupSites, isLoading: isReadingGroupLibraries } =
        useClientGroupLibraries(boundedGroupSites.sites, siteSettings, groupRefreshToken);

    // Descriptor + originating location, paired: the descriptor drives fetching, the
    // location is what "open this source" navigates to (a library OR a folder).
    const sourcePairs = React.useMemo<{ descriptor: IAggregatedLibraryDescriptor; location: ILocation }[]>(
        () => [
            ...(libraries ?? [])
                .filter(library => library?.sharePointListId)
                .map(library => ({
                    location: library as ILocation,
                    descriptor: {
                        listId: library.sharePointListId,
                        libraryName: library.displayName,
                        webUrl: library.siteUrl ?? siteWebUrl,
                        // For DocumentLibrary locations the location id IS the Graph drive id.
                        driveId: library.id,
                        siteName: siteDisplayName,
                    },
                })),
            ...(folders ?? [])
                .filter(folder => folder?.sharePointListId && folder?.path)
                .map(folder => ({
                    location: folder as ILocation,
                    descriptor: {
                        listId: folder.sharePointListId,
                        libraryName: folder.displayName,
                        webUrl: folder.siteUrl ?? siteWebUrl,
                        driveId: folder.driveId,
                        // Decoded server-relative path (webDavUrl-derived) — scopes this
                        // source to the folder subtree (FolderServerRelativeUrl body
                        // parameter on every stream request).
                        rootFolderServerRelativeUrl: folder.path,
                        siteName: siteDisplayName,
                    },
                })),
        ],
        [libraries, folders, siteWebUrl, siteDisplayName]
    );

    // Client-group libraries: rows of THEIR site (mapGroupSiteDrives sets siteUrl and name).
    const groupSourcePairs = React.useMemo<{ descriptor: IAggregatedLibraryDescriptor; location: ILocation }[]>(
        () => (groupLibraries ?? [])
            .filter(library => library?.sharePointListId && library?.siteUrl)
            .map(library => ({
                location: library as ILocation,
                descriptor: {
                    listId: library.sharePointListId,
                    libraryName: library.displayName,
                    webUrl: library.siteUrl,
                    driveId: library.id,
                    siteName: library.siteDisplayName,
                },
            })),
        [groupLibraries]
    );

    const libraryDescriptors = React.useMemo<IAggregatedLibraryDescriptor[]>(
        () => sourcePairs.map(pair => pair.descriptor),
        [sourcePairs]
    );
    const groupDescriptors = React.useMemo<IAggregatedLibraryDescriptor[]>(
        () => groupSourcePairs.map(pair => pair.descriptor),
        [groupSourcePairs]
    );
    const allSourcePairs = React.useMemo(() => [...sourcePairs, ...groupSourcePairs], [sourcePairs, groupSourcePairs]);
    const allDescriptors = React.useMemo(() => [...libraryDescriptors, ...groupDescriptors], [libraryDescriptors, groupDescriptors]);
    // Known as soon as the lookup answers, before their libraries are read; the first-paint hold
    // waits for both, so the Site column normally arrives with the first rows.
    const hasGroupSites: boolean = boundedGroupSites.sites.length > 0;

    // Per-LIST fan-out targets (filter value discovery probes whole lists): two
    // folder sources on one list must not probe it twice.
    const filterProbeDescriptors = React.useMemo<IAggregatedLibraryDescriptor[]>(
        () => allDescriptors.filter((descriptor, index, all) =>
            all.findIndex(other => other.listId === descriptor.listId) === index),
        [allDescriptors]
    );

    const {
        isLoading, isLoadingMore, isLoadingGroupSources, markRowsRendered, rows, mergedColumns, viewHiddenColumnKeys, hasMore, isCapped, failures, excludedByFilter,
        hasForceCheckoutLibrary, sort, setSortColumn, setSearchValue, filters, setFilters, loadMore, refresh, deleteItems,
        refreshItems, applyRealtimeCreate, applyRealtimeUpdate, applyRealtimeDelete, applyRealtimeLock,
    } = useAggregatedDocuments(libraryDescriptors, groupDescriptors, isReadingGroupLibraries || isResolvingGroupSites);

    // Refresh re-reads the group sites' libraries as well (one Graph batch), and the membership
    // that decides which sites those are.
    const refreshView = React.useCallback((): void => {
        refresh();
        if (boundedGroupSites.sites.length > 0) {
            setGroupRefreshToken(current => current + 1);
        }
        onRefreshGroupSites?.();
    }, [refresh, boundedGroupSites, onRefreshGroupSites]);

    const [selectedItems, setSelectedItems] = React.useState<{ items: any[]; count: number }>({ items: [], count: 0 });
    const [contextMenuState, setContextMenuState] = React.useState<{ open: boolean; position: Point | null }>({ open: false, position: null });
    // Library (and Site) are client-side concepts (not SharePoint fields), so sorting by them
    // stays a local sort over the loaded window; every other column sorts server-side.
    const [librarySort, setLibrarySort] = React.useState<{ active: boolean; descending: boolean; columnKey?: string }>({ active: false, descending: true });

    // Tells the hook how many rows the grid has rendered: a client-group join keeps those on
    // screen and spends no requests on rows nobody has seen. Under a Library/Site sort the grid
    // shows the loaded rows in another order, so every loaded row counts as seen.
    const isLocalSortRef = React.useRef<boolean>(false);
    isLocalSortRef.current = librarySort.active;
    const onRowDidMount = React.useCallback((_item?: any, index?: number): void => {
        markRowsRendered(isLocalSortRef.current ? Number.MAX_SAFE_INTEGER : (index ?? 0) + 1);
    }, [markRowsRendered]);
    const [hiddenColumnKeys, setHiddenColumnKeys] = React.useState<string[]>(
        () => (CacheHelper.get(hiddenColumnsCacheKey(cacheScopeId), true) as string[]) ?? []);
    // Session-only override: user explicitly re-checked a column the auto-hider
    // deselected for being empty. Never persisted — persisting either direction of
    // data-derived visibility would pin a stale verdict across sessions.
    const [forcedVisibleKeys, setForcedVisibleKeys] = React.useState<string[]>([]);
    // Sticky per-session memory of columns ever seen with a value: emptiness may only
    // ever relax, so filtering/searching (rows become a server-filtered subset) or
    // refreshing can never make a populated column vanish mid-session.
    const everNonEmptyKeysRef = React.useRef<Set<string>>(new Set());
    const autoHiddenKeysRef = React.useRef<Set<string>>(new Set());
    const [searchText, setSearchText] = React.useState<string>("");
    const [isDeleteDialogHidden, setIsDeleteDialogHidden] = React.useState<boolean>(true);
    const [isDeleting, setIsDeleting] = React.useState<boolean>(false);
    const [deleteFailedCount, setDeleteFailedCount] = React.useState<number>(0);
    const [isFilterPanelOpen, setIsFilterPanelOpen] = React.useState<boolean>(false);
    // Automation result the tenant's hooks pushed over SignalR for THIS user, shown in
    // the same web panel the library view uses.
    const [externalResourceUrl, setExternalResourceUrl] = React.useState<IExternalResourceUrl | null>(null);

    const _selection = React.useRef(new Selection({
        getKey: getRowKey,
        // The load-more shimmer sentinel is a literal null item — it must never be
        // selectable or select-all/marquee would poison every all-rows command gate.
        // A realtime-locked row is equally unselectable: automation owns it until the
        // next broadcast, so no command may target it.
        canSelectItem: (item: any) => Boolean(item) && item[rowLockIndicator] !== true,
        onSelectionChanged: () => {
            setSelectedItems({ items: _selection.current.getSelection(), count: _selection.current.getSelectedCount() });
        }
    }));

    // Debounced cross-library server-side search (InplaceSearchQuery per cursor).
    React.useEffect(() => {
        const handle = setTimeout(() => setSearchValue(searchText.trim()), SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(handle);
    }, [searchText, setSearchValue]);

    React.useEffect(() => {
        setHiddenColumnKeys((CacheHelper.get(hiddenColumnsCacheKey(cacheScopeId), true) as string[]) ?? []);
        setForcedVisibleKeys([]);
        everNonEmptyKeysRef.current = new Set();
        autoHiddenKeysRef.current = new Set();
    }, [cacheScopeId]);

    const openItemInBrowser = React.useCallback(async (item: any): Promise<void> => {
        try {
            if (!item) {
                return;
            }
            // Library parity: PDFs open in the Docubird PDF viewer (the same
            // OpenPdfInBrowser routing as the library grid's name link and every
            // other surface). Aggregate rows are never OneDrive. Falls through to
            // the SharePoint web view when the Graph ids are unavailable.
            if (item.File_x0020_Type?.toLowerCase() === extensions.pdf && item.driveId && item.itemId) {
                OpenPdfInBrowser(await authModule.getAccount(false), item.driveId, item.itemId, false);
                return;
            }
            const embedUrl: string = item.ServerRedirectedEmbedUrl;
            // Per-segment encoding: encodeURI leaves `#` raw (fragment cut) and
            // double-encodes `%` — both are legal in SharePoint file names.
            const encodedPath = String(item.FileRef ?? "").split("/").map(encodeURIComponent).join("/");
            const url = embedUrl?.length > 0
                ? embedUrl
                : `${new URL(item[AGGREGATE_SOURCE_WEB_URL]).origin}${encodedPath}?web=1`;
            window.open(url, "_blank");
        } catch (error) {
            logger(error, "ContentBodyAllDocuments.tsx (openItemInBrowser)");
        }
    }, []);

    // Resolves the row's SOURCE (library or Tess dossier folder) and navigates to it.
    // Source-key match first — a list id alone is ambiguous when folder sources share a
    // list; the list-id fallback covers rows from a source that has since vanished.
    const openSourceLibrary = React.useCallback((item: any): void => {
        const sourceKey = item?.[AGGREGATE_SOURCE_KEY];
        const pair = allSourcePairs.find(candidate => getAggregatedSourceKey(candidate.descriptor) === sourceKey)
            ?? allSourcePairs.find(candidate => candidate.descriptor.listId === item?.[AGGREGATE_SOURCE_LIST_ID]);
        if (pair) {
            onOpenLibrary(pair.location);
        }
    }, [allSourcePairs, onOpenLibrary]);

    const clearSelection = React.useCallback((): void => {
        _selection.current.setAllSelected(false);
    }, []);

    // Realtime (SignalR ItemHub), the aggregate twin of the library grid's connection:
    // ONE socket enlisted for EVERY library on the site, each broadcast routed back to
    // its source library's rows. Gated exactly like the library grid — tenant automation
    // hooks, or any displayed library forcing checkout (Docubird's own checkout and PDF
    // windows broadcast item updates for those).
    const realtimeListIds = React.useMemo(
        // Unique: hub groups are per LIST, and several folder sources may share one. Client-group
        // libraries are enlisted on the open connection when they arrive (no rebuild).
        () => Array.from(new Set(allDescriptors.map(descriptor => descriptor.listId))),
        [allDescriptors]
    );
    const isRealtimeEnabled = React.useMemo(
        () => CacheHelper.get(DocubirdCache.signalREnabled) === true || hasForceCheckoutLibrary,
        [hasForceCheckoutLibrary]
    );

    useAggregatedItemHub({
        listIds: realtimeListIds,
        enabled: isRealtimeEnabled,
        currentAccountId: userAccount?.accountId,
        externalContentLabel: languageStrings.ExternalContent,
        handlers: {
            onCreated: applyRealtimeCreate,
            onDeleted: applyRealtimeDelete,
            onLocked: applyRealtimeLock,
            onUpdated: applyRealtimeUpdate,
            onExternalResource: setExternalResourceUrl,
        },
    });

    // THE set every command acts on: the selection re-resolved against the CURRENT rows,
    // never the objects Fluent handed us at selection time (stale after a realtime
    // re-read), and never a row that vanished or got locked. See the helper's doc for why
    // each of the three cases is unavoidable.
    const commandableItems = React.useMemo<any[]>(
        () => resolveCommandableItems(selectedItems.items, rows, getRowKey),
        [selectedItems, rows]
    );

    // "N selected" + clear, from the SAME hook the library grid uses, so the wording,
    // the icon and the clear behaviour cannot drift between the two grids.
    //
    // The count is the commandable one, which is the exact analogue of the library's:
    // its getSelectionDetails also strips locked rows before counting. Fluent cannot
    // deselect a locked row (setIndexSelected no-ops once canSelectItem rejects it), so
    // counting the raw selection would report rows no command can touch.
    const { menuItem_SelectedFileCount } = useMenuItem_SelectedFileCount(
        commandableItems.length,
        // The library passes its global busy flag to hide the count mid-operation. This
        // view has no equivalent — its long-running commands own their own dialogs.
        false,
        clearSelection
    );

    // Folder locations extend ISharepointListLocation, and every command-layer lookup
    // (settings, panel contexts, display names, hook validation) is per LIST — so folder
    // sources resolve through the same array. Libraries first: on a shared list the
    // list-shaped entry should win the find(). Client-group libraries carry their own site
    // (siteUrl, siteDisplayName), so their commands target and validate against that site.
    const commandLocations = React.useMemo<ISharepointListLocation[]>(
        () => [...(libraries ?? []), ...((folders ?? []) as ISharepointListLocation[]), ...(groupLibraries ?? [])],
        [libraries, folders, groupLibraries]
    );

    const { slots: commandSlots, panels: commandPanels, openPropertiesPanel } = useAggregatedCommands({
        selectedItems: commandableItems,
        libraries: commandLocations,
        siteWebUrl,
        siteId,
        siteDisplayName,
        openItemInBrowser,
        refreshItems,
        clearSelection,
    });

    // Share with group: files of one site, shared with the groups of that site (see the hook).
    const { shareWithGroup, shareWithGroupPanel } = useAggregatedShareWithGroup({
        selectedItems: commandableItems,
        siteId,
        siteWebUrl,
        siteDisplayName,
        groupSites,
    });

    // F2 = the Rename command. buildAggregateMenuItems places the rename slot
    // unconditionally, so the slot IS what the menus show.
    const renameShortcutGridRef = useRenameShortcut(commandSlots.rename);

    const downloadItems = React.useCallback((items: any[]): void => {
        // Staggered, not looped synchronously: browsers treat N back-to-back anchor
        // clicks from one gesture as automatic multiple downloads and drop all but
        // the first. Spacing them out lets each register as its own download.
        (items ?? []).filter(Boolean).forEach((item, index) => {
            setTimeout(() => {
                try {
                    const url = `${item[AGGREGATE_SOURCE_WEB_URL]}/_layouts/15/download.aspx?SourceUrl=${encodeURIComponent(item.FileRef)}`;
                    const anchor = document.createElement("a");
                    anchor.href = url;
                    anchor.rel = "noopener";
                    anchor.style.display = "none";
                    document.body.appendChild(anchor);
                    anchor.click();
                    anchor.remove();
                } catch (error) {
                    logger(error, "ContentBodyAllDocuments.tsx (downloadItems)");
                }
            }, index * 500);
        });
    }, []);

    const confirmDelete = React.useCallback(async (): Promise<void> => {
        setIsDeleting(true);
        try {
            const failed = await deleteItems(commandableItems);
            setDeleteFailedCount(failed);
            _selection.current.setAllSelected(false);
        } finally {
            setIsDeleting(false);
            setIsDeleteDialogHidden(true);
        }
    }, [deleteItems, commandableItems]);

    // Columns blank in every loaded row auto-deselect (verdict only ever relaxes via
    // the sticky seen-non-empty set). rows === [] means loading/refresh, not evidence
    // of emptiness — keep the last verdict so columns don't flash during reloads.
    const autoHiddenKeys = React.useMemo<Set<string>>(() => {
        if (rows.length === 0) {
            return autoHiddenKeysRef.current;
        }
        const emptyKeys = getEmptyColumnKeys(mergedColumns, rows);
        mergedColumns.forEach(column => {
            if (!emptyKeys.has(column.key)) {
                everNonEmptyKeysRef.current.add(column.key);
            }
        });
        const next = new Set<string>();
        emptyKeys.forEach(key => {
            if (!everNonEmptyKeysRef.current.has(key)) {
                next.add(key);
            }
        });
        autoHiddenKeysRef.current = next;
        return next;
    }, [mergedColumns, rows]);

    // Everything data-derived that hides a column by default: all-empty in the loaded
    // window, or displayed in NO library's default view. Both are derived-only signals
    // — the persisted cache never sees them.
    const derivedHiddenKeys = React.useMemo<Set<string>>(() => {
        const combined = new Set(autoHiddenKeys);
        viewHiddenColumnKeys.forEach(key => combined.add(key));
        return combined;
    }, [autoHiddenKeys, viewHiddenColumnKeys]);

    // Effective visibility = user intent (persisted) ∪ derived hides, with the
    // session force-visible override winning over the derived hides. Grid and column
    // picker both read THIS set so they can never disagree.
    const effectiveHiddenKeys = React.useMemo<Set<string>>(() => {
        const hidden = new Set(hiddenColumnKeys);
        derivedHiddenKeys.forEach(key => {
            if (!forcedVisibleKeys.includes(key)) {
                hidden.add(key);
            }
        });
        return hidden;
    }, [hiddenColumnKeys, derivedHiddenKeys, forcedVisibleKeys]);

    // Always flips EFFECTIVE visibility, whichever mechanism is hiding the column —
    // otherwise a derived-hidden column's picker entry would be a dead checkbox (its
    // key was never in hiddenColumnKeys to remove). Only user-hides persist.
    const toggleColumnVisibility = React.useCallback((columnKey: string): void => {
        if (effectiveHiddenKeys.has(columnKey)) {
            if (hiddenColumnKeys.includes(columnKey)) {
                const next = hiddenColumnKeys.filter(key => key !== columnKey);
                CacheHelper.set(hiddenColumnsCacheKey(cacheScopeId), next, true);
                setHiddenColumnKeys(next);
            }
            if (derivedHiddenKeys.has(columnKey)) {
                setForcedVisibleKeys(current => current.includes(columnKey) ? current : [...current, columnKey]);
            }
        } else if (derivedHiddenKeys.has(columnKey)) {
            // Visible only through the force override — dropping it re-derives hidden
            // without polluting the persisted user-hidden list.
            setForcedVisibleKeys(current => current.filter(key => key !== columnKey));
        } else {
            const next = [...hiddenColumnKeys, columnKey];
            CacheHelper.set(hiddenColumnsCacheKey(cacheScopeId), next, true);
            setHiddenColumnKeys(next);
        }
    }, [cacheScopeId, effectiveHiddenKeys, hiddenColumnKeys, derivedHiddenKeys]);

    const showAllColumns = React.useCallback((): void => {
        setHiddenColumnKeys([]);
        CacheHelper.set(hiddenColumnsCacheKey(cacheScopeId), [], true);
        setForcedVisibleKeys(Array.from(derivedHiddenKeys));
    }, [cacheScopeId, derivedHiddenKeys]);

    // Distinct from "show all": drops the user's hides AND the session force-shows,
    // returning to the derived defaults (view-backed columns, empties deselected).
    const resetColumnsToDefault = React.useCallback((): void => {
        setHiddenColumnKeys([]);
        CacheHelper.set(hiddenColumnsCacheKey(cacheScopeId), [], true);
        setForcedVisibleKeys([]);
    }, [cacheScopeId]);

    const visibleMergedColumns = React.useMemo(
        () => mergedColumns.filter(column => !effectiveHiddenKeys.has(column.key)),
        [mergedColumns, effectiveHiddenKeys]
    );

    const sortedRows = React.useMemo(() => {
        if (!librarySort.active) {
            return rows; // server-sorted by the hook's k-way merge
        }
        const byName = (field: string) => (a: any, b: any): number =>
            String(a[field] ?? "").localeCompare(String(b[field] ?? ""), undefined, { sensitivity: "base" });
        const byLibrary = byName(AGGREGATE_SOURCE_LIBRARY_NAME);
        const bySite = byName(AGGREGATE_SOURCE_SITE_NAME);
        const localSortKey = librarySort.columnKey ?? "aggLibrary";
        const sorted = rows.slice().sort((a, b) =>
            (localSortKey === "aggSite" ? bySite(a, b) || byLibrary(a, b) : byLibrary(a, b))
            || String(a[AGGREGATE_ROW_KEY]).localeCompare(String(b[AGGREGATE_ROW_KEY])));
        return librarySort.descending ? sorted.reverse() : sorted;
    }, [rows, librarySort]);

    const onColumnHeaderClick = React.useCallback((_ev?: React.MouseEvent<HTMLElement>, column?: IColumn): void => {
        if (!column || column.key === "aggDocIcon") {
            return;
        }
        if (column.key === "aggLibrary" || column.key === "aggSite") {
            setLibrarySort(current => ({
                active: true,
                columnKey: column.key,
                descending: current.active && (current.columnKey ?? "aggLibrary") === column.key ? !current.descending : false,
            }));
            return;
        }
        setLibrarySort({ active: false, descending: true });
        setSortColumn(column.key);
    }, [setSortColumn]);

    const gridColumns = React.useMemo<IColumn[]>(() => {
        const columns = buildAggregatedGridColumns({
            mergedColumns: visibleMergedColumns,
            onOpenItem: openItemInBrowser,
            onOpenLibrary: openSourceLibrary,
            headers: {
                name: languageStrings.Name,
                library: languageStrings.DocumentLibrary,
                modified: languageStrings.Modified,
                modifiedBy: languageStrings.ModifiedBy,
                // The Site column shows whenever the site has client-group sites.
                site: hasGroupSites ? languageStrings.SharepointSite : undefined,
            },
        });
        return columns.map(column => {
            const isSorted = librarySort.active ? column.key === (librarySort.columnKey ?? "aggLibrary") : column.key === sort.columnKey;
            return {
                ...column,
                isSorted,
                isSortedDescending: isSorted ? (librarySort.active ? librarySort.descending : sort.descending) : undefined,
            };
        });
    }, [visibleMergedColumns, openItemInBrowser, openSourceLibrary, languageStrings, sort, librarySort, hasGroupSites]);

    // View-scoped slots + component-owned row slots (download/delete), merged with the
    // composer's row-scoped slots. Everything flows through the pure builder so the
    // command bar and context menu stay consistent.
    const { commandBarItems, commandBarFarItems, contextMenu } = React.useMemo(() => {
        const selected = commandableItems;
        const everySelected = (predicate: (item: any) => boolean): boolean =>
            selected.length > 0 && selected.every(predicate);

        const download: IContextualMenuItem | null = everySelected(item => item?.EffectiveBasePermissions?.canOpenItems === true)
            ? {
                key: "aggDownload",
                text: languageStrings.Download,
                iconProps: {},
                onRenderIcon: () => <ArrowDownload20Regular className={menuItemIconStyles} />,
                onClick: () => downloadItems(selected),
            }
            : null;

        const deleteItem: IContextualMenuItem | null = everySelected(item => item?.EffectiveBasePermissions?.canDeleteListItem === true)
            ? {
                key: "aggDelete",
                text: languageStrings.Delete,
                iconProps: {},
                onRenderIcon: () => <Delete20Regular className={menuItemIconStyles} />,
                onClick: () => setIsDeleteDialogHidden(false),
            }
            : null;

        // Inline quick-filter box, the library view's pattern (useMenuItem_QuickFilter):
        // rendered as the LAST primary bar item, hidden while rows are selected so
        // selection commands get the space. Controlled from parent state (unlike the
        // library's local-state box) because a server-side search must survive the box
        // unmounting on selection — and stay clearable when it filters down to 0 rows.
        const quickFilter: IContextualMenuItem | null =
            (rows.length > 0 || searchText.length > 0) && commandableItems.length === 0
                ? {
                    key: "aggQuickFilter",
                    onRender: () => (
                        <QuickFilterSearchBox
                            styles={quickFilterBoxStyles}
                            placeholder={languageStrings.SearchAllDocuments}
                            underlined={true}
                            value={searchText}
                            onChange={(_ev, newValue) => setSearchText(newValue ?? "")}
                            onClear={() => setSearchText("")}
                            showIcon={true}
                        />
                    ),
                }
                : null;

        const filterItem: IContextualMenuItem = {
            key: "aggFilter",
            iconProps: {},
            onRenderIcon: () => <Filter20Regular className={menuItemIconStyles} />,
            iconOnly: true,
            ariaLabel: languageStrings.Filter,
            tooltipHostProps: { content: languageStrings.Filter },
            onClick: () => setIsFilterPanelOpen(true),
        };

        // Mirrors the single-library grid: an explicit clear-filter command appears
        // while filters are active (its presence is also the "filters on" indicator).
        const clearFilterItem: IContextualMenuItem | null = filters.length > 0
            ? {
                key: "aggClearFilter",
                iconProps: {},
                onRenderIcon: () => <FilterDismiss20Regular className={menuItemIconStyles} />,
                iconOnly: true,
                ariaLabel: languageStrings.ClearFilter,
                tooltipHostProps: { content: languageStrings.ClearFilter },
                onClick: () => setFilters([]),
            }
            : null;

        const columnsItem: IContextualMenuItem | null = mergedColumns.length > 0
            ? {
                key: "aggColumns",
                // No SVG equivalent exists for this one — force the neutral foreground
                // on both glyph and submenu chevron so it matches the SVG-based items.
                iconProps: { iconName: "TripleColumn", styles: { root: { color: defaultFontColor } } },
                buttonStyles: { menuIcon: { color: defaultFontColor } },
                iconOnly: true,
                ariaLabel: languageStrings.Columns,
                tooltipHostProps: { content: languageStrings.Columns },
                subMenuProps: {
                    // Column-chooser convention (Outlook field chooser / grid libraries):
                    // plain-text actions — NO icons, so their text aligns with the check
                    // gutter of the toggle rows — then a divider, then the checkbox list.
                    items: [
                        {
                            key: "aggColumnsShowAll",
                            text: languageStrings.ShowAllColumns,
                            onClick: (ev) => { ev?.preventDefault(); showAllColumns(); },
                        },
                        {
                            key: "aggColumnsReset",
                            text: languageStrings.ResetToDefault,
                            onClick: (ev) => { ev?.preventDefault(); resetColumnsToDefault(); },
                        },
                        { key: "aggColumnsDivider", itemType: ContextualMenuItemType.Divider },
                        ...mergedColumns.map((column): IContextualMenuItem => ({
                            key: `aggColumnToggle_${column.key}`,
                            text: column.displayName,
                            // Explains WHY an auto-deselected empty column is unchecked
                            // (stays while force-shown — the column is still empty).
                            secondaryText: autoHiddenKeys.has(column.key) ? languageStrings.EmptyColumnHint : undefined,
                            canCheck: true,
                            checked: !effectiveHiddenKeys.has(column.key),
                            onClick: (ev) => { ev?.preventDefault(); toggleColumnVisibility(column.key); },
                        })),
                    ],
                },
            }
            : null;

        // View-switcher dropdown, à la SharePoint's view selector: labeled with the
        // CURRENT view, chevron opens the list, checkmark marks the active entry.
        // Twin of the locations view's switcher — same key order, both pinned last.
        const switchToLocations: IContextualMenuItem = {
            key: "aggSwitchToLocations",
            text: languageStrings.AllDocuments,
            iconProps: {},
            onRenderIcon: () => <List20Regular className={menuItemIconStyles} />,
            // Submenu chevron is a font glyph — force the neutral foreground like the
            // columns item, or it renders in the theme accent color.
            buttonStyles: { menuIcon: { color: defaultFontColor } },
            ariaLabel: languageStrings.AllDocuments,
            subMenuProps: {
                items: [
                    {
                        key: "aggViewLocations",
                        text: languageStrings.LocationsView,
                        canCheck: true,
                        checked: false,
                        onClick: () => onSwitchToLocations(),
                    },
                    {
                        key: "aggViewAllDocuments",
                        text: languageStrings.AllDocuments,
                        canCheck: true,
                        checked: true,
                    },
                ],
            },
        };

        const refreshItem: IContextualMenuItem = {
            key: "aggRefresh",
            iconProps: {},
            onRenderIcon: () => <ArrowClockwise20Regular className={menuItemIconStyles} />,
            iconOnly: true,
            ariaLabel: languageStrings.Refresh,
            tooltipHostProps: { content: languageStrings.Refresh },
            onClick: () => refreshView(),
        };

        return buildAggregateMenuItems({
            ...commandSlots,
            shareWithGroup,
            download,
            delete: deleteItem,
            quickFilter,
            filter: filterItem,
            clearFilter: clearFilterItem,
            columns: columnsItem,
            refresh: refreshItem,
            switchToLocations,
            selectedFileCount: menuItem_SelectedFileCount,
        });
    }, [commandableItems, languageStrings, mergedColumns, effectiveHiddenKeys, autoHiddenKeys, filters.length, commandSlots, shareWithGroup,
        searchText, rows.length, setFilters, menuItem_SelectedFileCount,
        downloadItems, toggleColumnVisibility, showAllColumns, resetColumnsToDefault, onSwitchToLocations, refreshView]);

    const onItemContextMenu = React.useCallback((item?: any, _index?: number, ev?: Event): boolean => {
        const mouseEvent = ev as MouseEvent;
        // A realtime-locked row has no commands (it cannot even be selected), so its
        // menu would be empty at best and misleading at worst.
        if (item?.[rowLockIndicator] === true) {
            return false;
        }
        if (item && mouseEvent) {
            const key = getRowKey(item);
            if (key && !_selection.current.isKeySelected(key)) {
                _selection.current.setAllSelected(false);
                _selection.current.setKeySelected(key, true, false);
            }
            setContextMenuState({ open: true, position: { left: mouseEvent.clientX, top: mouseEvent.clientY } });
        }
        return false;
    }, []);

    // Realtime lock: automation is processing the item server-side, so the row renders
    // greyed out and inert until the next broadcast re-reads it — same treatment the
    // library grid gives its DetailsRow.
    const onRenderRow = React.useCallback((rowProps?: IDetailsRowProps, defaultRender?: (props?: IDetailsRowProps) => JSX.Element | null): JSX.Element | null => {
        if (!rowProps || !defaultRender) {
            return null;
        }
        // Through defaultRender (not a hand-built DetailsRow) so DetailsList keeps
        // composing the row it wants to. The null load-more sentinel never reaches here
        // — DetailsList routes falsy items to onRenderMissingItem, which is where
        // ShimmeredDetailsList's placeholder (and our loadMore trigger) live.
        return defaultRender({ ...rowProps, disabled: rowProps.item?.[rowLockIndicator] === true });
    }, []);

    const onRenderCustomPlaceholder = React.useCallback((rowProps: IDetailsRowProps, _index?: number, defaultRender?: (props: IDetailsRowProps) => React.ReactNode) => {
        if (rowProps.item === null) {
            // Deferred: loadMore setStates, and this callback runs during the list's
            // render phase — calling it synchronously is an update-during-render.
            // Re-entry while pending is guarded inside the hook (isLoadingMoreRef).
            setTimeout(loadMore, 0);
        }
        return defaultRender ? defaultRender(rowProps) : null;
    }, [loadMore]);

    const gridItems = React.useMemo(
        () => (hasMore ? [...sortedRows, null] : sortedRows),
        [sortedRows, hasMore]
    );

    // Split by cause, because only one of them is actionable. A library over the list
    // view threshold needs an index on the column being sorted or filtered — folding it
    // into the same "could not load" as a 403 or a timeout hides the one fix the user
    // could actually apply. Both bars can show at once when a site has both problems.
    //
    // isMultiline on the threshold bar: unlike the others its text carries an
    // instruction, and a single-line MessageBar truncates it to the library names.
    const thresholdFailures = failures.filter(failure => failure.reason === "throttled");
    const otherFailures = failures.filter(failure => failure.reason !== "throttled");
    // A client group site whose libraries could not be read at all is named with them.
    const failedNames: string[] = [
        ...otherFailures.map(failure => failure.libraryName),
        ...failedGroupSites.map(groupSiteDisplayName).filter(name => name.length > 0),
    ];

    const thresholdBar = thresholdFailures.length > 0 && (
        <MessageBar messageBarType={MessageBarType.warning} isMultiline={true}>
            {languageStrings.LibrariesExceededThreshold.replace("{placeholder}", thresholdFailures.map(failure => failure.libraryName).join(", "))}
        </MessageBar>
    );

    const failureBar = failedNames.length > 0 && (
        <MessageBar messageBarType={MessageBarType.warning} isMultiline={false}>
            {languageStrings.LibrariesFailedToLoad.replace("{placeholder}", failedNames.join(", "))}
        </MessageBar>
    );

    const excludedBar = excludedByFilter.length > 0 && (
        <MessageBar messageBarType={MessageBarType.info} isMultiline={false}>
            {languageStrings.FilterExcludesLibraries.replace("{placeholder}", excludedByFilter.join(", "))}
        </MessageBar>
    );

    // isMultiline, like the threshold bar: this text now states a caveat as well as a
    // number, and a single-line MessageBar would truncate away exactly the caveat.
    //
    // The caveat is real. Hitting the cap marks every cursor exhausted, which deactivates
    // the frontier and flushes the whole buffer into view — so nothing is hidden, but past
    // the old frontier the list is no longer a true merge: each cursor holds a PREFIX of
    // its library, so rows that would have interleaved from unfetched pages are simply
    // absent. Correctly ordered, incomplete.
    const cappedBar = isCapped && (
        <MessageBar messageBarType={MessageBarType.info} isMultiline={true}>
            {languageStrings.AllDocumentsCapReached.replace("{placeholder}", `${AGGREGATED_MAX_TOTAL_ROWS}`)}
        </MessageBar>
    );

    // A client group larger than the view reads: say how many of its sites are included.
    const groupSitesCappedBar = boundedGroupSites.total > MAX_AGGREGATED_GROUP_SITES && (
        <MessageBar messageBarType={MessageBarType.info} isMultiline={false}>
            {languageStrings.ClientGroupSitesCapped
                .replace("{0}", `${MAX_AGGREGATED_GROUP_SITES}`)
                .replace("{1}", `${boundedGroupSites.total}`)}
        </MessageBar>
    );

    // The site's own documents are on screen and the client group's are still joining. Drawn
    // over the grid's top edge, so it never moves the rows when it comes and goes.
    const isJoiningGroupSites: boolean = !isLoading && !isLoadingLibraries && (isReadingGroupLibraries || isLoadingGroupSources);

    const deleteFailedBar = deleteFailedCount > 0 && (
        <MessageBar
            messageBarType={MessageBarType.error}
            isMultiline={false}
            onDismiss={() => setDeleteFailedCount(0)}
            dismissButtonAriaLabel="Close"
        >
            {`${languageStrings.ActionFailed} (${deleteFailedCount})`}
        </MessageBar>
    );

    const deleteDialog = !isDeleteDialogHidden && (
        <Dialog
            hidden={false}
            onDismiss={() => !isDeleting && setIsDeleteDialogHidden(true)}
            dialogContentProps={{
                type: DialogType.normal,
                title: languageStrings.Delete,
                subText: languageStrings.ConfirmDeleteDocuments.replace("{placeholder}", `${commandableItems.length}`),
            }}
        >
            <DialogFooter>
                <PrimaryButton disabled={isDeleting} onClick={confirmDelete} text={languageStrings.Delete} />
                <DefaultButton disabled={isDeleting} onClick={() => setIsDeleteDialogHidden(true)} text={languageStrings.Cancel} />
            </DialogFooter>
        </Dialog>
    );

    return (
        <>
            <CommandBar
                styles={commandBarStyle}
                items={commandBarItems}
                farItems={commandBarFarItems}
                ariaLabel="Use left and right arrow keys to navigate between commands"
            />
            <div className={classNames.viewport}>
            {thresholdBar}
            {failureBar}
            {excludedBar}
            {cappedBar}
            {groupSitesCappedBar}
            {deleteFailedBar}
            <div className={classNames.wrapper} ref={renameShortcutGridRef}>
                {isJoiningGroupSites && (
                    <ProgressIndicator
                        ariaValueText={languageStrings.LoadingClientGroupSites}
                        barHeight={2}
                        styles={groupProgressStyles}
                    />
                )}
                {/* Fluent's ScrollablePane only enables vertical overflow on its content
                    container; without overflowX the aggregate grid (which is wider than
                    the pane by design) clips instead of scrolling horizontally. Header
                    and rows scroll together (matches the locations view; no Sticky).
                    fixedColumns keeps the total grid width deterministic (justified
                    squeezes columns to the viewport instead of overflowing). */}
                <ScrollablePane
                    scrollbarVisibility={ScrollbarVisibility.auto}
                    styles={{ contentContainer: { overflowX: "auto" } }}
                >
                    <MarqueeSelection selection={_selection.current}>
                        <ShimmeredDetailsList
                            compact={false}
                            items={gridItems}
                            columns={gridColumns}
                            selectionMode={SelectionMode.multiple}
                            constrainMode={ConstrainMode.unconstrained}
                            layoutMode={DetailsListLayoutMode.fixedColumns}
                            selection={_selection.current}
                            // REQUIRED, and the library grid passes the same constant:
                            // without setKey, DetailsList computes shouldResetSelection
                            // as true on EVERY items change and clears the selection.
                            // Rows now change on their own (realtime broadcasts), so a
                            // background edit anywhere on the site would wipe a
                            // multi-selection mid-workflow — and even scrolling
                            // (loadMore) did. A constant key preserves selection BY KEY
                            // instead; an empty items array (every reload) still clears.
                            setKey="multiple"
                            selectionPreservedOnEmptyClick={true}
                            enableShimmer={isLoading || isLoadingLibraries}
                            getKey={getRowKey}
                            onColumnHeaderClick={onColumnHeaderClick}
                            // Library parity: double-click opens the properties panel
                            // (onItemInvoked → showFileInfo there). The invoking click
                            // selects the row, so the composer's `single` is this item;
                            // a multi-select invoke is auto-reset by the composer.
                            onItemInvoked={(item?: any) => item && item[rowLockIndicator] !== true && openPropertiesPanel()}
                            onItemContextMenu={onItemContextMenu}
                            onRenderRow={onRenderRow}
                            onRowDidMount={onRowDidMount}
                            onRenderCustomPlaceholder={onRenderCustomPlaceholder}
                            isHeaderVisible={true}
                            ariaLabelForSelectionColumn="Toggle selection"
                            ariaLabelForSelectAllCheckbox="Toggle selection for all items"
                            checkButtonAriaLabel="Row checkbox"
                        />
                    </MarqueeSelection>
                </ScrollablePane>
            </div>
            </div>
            {contextMenuState.open && (
                <ContextualMenu
                    target={contextMenuState.position}
                    directionalHint={DirectionalHint.rightTopEdge}
                    directionalHintFixed={true}
                    items={contextMenu}
                    onDismiss={() => setContextMenuState({ open: false, position: null })}
                />
            )}
            {deleteDialog}
            {/* Automation finished and produced a URL for THIS user (SignalR
                ExternalResource) — same panel treatment as the library view. */}
            {externalResourceUrl && <WebPanel
                isOpen={true}
                width="1100px"
                closePanel={() => setExternalResourceUrl(null)}
                headerText={externalResourceUrl.description}
                styles={previewWebPanelStyles}
            >
                <div style={{ height: "calc(100vh - 80px)", width: "100%", marginTop: 5 }}>
                    <iframe style={{ height: "100%", width: "100%", border: "none", overflow: "hidden" }} src={externalResourceUrl.url} />
                </div>
            </WebPanel>}
            {commandPanels}
            {shareWithGroupPanel}
            {/* Kept MOUNTED (only panelIsOpen toggles): the panel accumulates
                server-discovered filter values and expanded state that must survive
                close/reopen — unmounting would shrink a filtered section to just its
                applied value, with no way to OR-in a second one. */}
            <CMAggregatedFilterPanel
                panelIsOpen={isFilterPanelOpen}
                closePanel={() => setIsFilterPanelOpen(false)}
                mergedColumns={mergedColumns}
                // Per-list, not per-source: the panel's value probes scan whole lists,
                // so two folder sources on one list must not fan out twice.
                libraryDescriptors={filterProbeDescriptors}
                rows={rows}
                filters={filters}
                applyFilters={setFilters}
                isLoading={isLoading}
            />
        </>
    );
};
